// NotificationHeader.tsx

import { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useSocket } from "@/context/SocketProvider";
import { notificationService } from "@/services/notificationService";

export interface NotificationPayload {
  id: string;
  title: string;
  message: string;
  timestamp: string;
}

interface GlobalNotificationListenerProps {
  userId?: string;
  deviceToken?: string; // Optional: FCM or Web Push token
  categories?: string[];
}

export const GlobalNotificationListener = ({
  userId,
  deviceToken,
  categories = ["general"],
}: GlobalNotificationListenerProps) => {
  const { socket } = useSocket();
  const { enqueueSnackbar } = useSnackbar();

  // 1. Register device/preferences with MongoDB via REST on mount if user is logged in
  useEffect(() => {
    if (!userId || !deviceToken) return;

    const registerNotificationSettings = async () => {
      try {
        await notificationService.registerDevice({
          userId,
          deviceToken,
          categories,
          isEnabled: true,
        });
      } catch (error) {
        console.error("Failed to register push notification settings:", error);
      }
    };

    registerNotificationSettings();
  }, [userId, deviceToken, categories]);

  // 2. Handle Real-time WebSocket Notifications
  useEffect(() => {
    if (!socket || !userId) return;

    // Join the target user room upon socket connection
    socket.emit("joinUserRoom", userId);

    const handleNotification = (data: NotificationPayload) => {
      // Fires the notistack toast globally
      enqueueSnackbar(`${data.title}: ${data.message}`, {
        variant: "info",
        autoHideDuration: 4000,
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
    };

    socket.on("notification", handleNotification);

    return () => {
      socket.off("notification", handleNotification);
    };
  }, [socket, userId, enqueueSnackbar]);

  return null; // Headless component, renders no DOM elements
};
